import {
  CONTENT_FIT_TYPES,
  CONTENT_GROW_TYPES,
  FOOTPRINT_GROW_TYPES,
  DEFAULT_FIT_FLOOR_ROWS,
  FIT_FLOOR_ROWS,
  GRID_COLS,
  GRID_GUTTER,
  GRID_MARGIN,
  GRID_ROW_PITCH,
  HEADER_ROWS,
  widgetCells,
  widgetMinCells,
} from "./tokens/layout"

// Free dashboards keep main's 4px snap; report pages (studio) use the 24-column grid below.
export const DEFAULT_GRID_SIZE = 4

export function snapValue(value, gridSize = DEFAULT_GRID_SIZE) {
  const size = Math.max(1, gridSize || DEFAULT_GRID_SIZE)
  return Math.round(value / size) * size
}

export function snapLayout(layout, gridSize = DEFAULT_GRID_SIZE) {
  return {
    x: snapValue(layout.x, gridSize),
    y: snapValue(layout.y, gridSize),
    w: Math.max(gridSize, snapValue(layout.w, gridSize)),
    h: Math.max(gridSize, snapValue(layout.h, gridSize)),
  }
}

export function rectanglesIntersect(first, second) {
  return (
    first.x < second.x + second.w &&
    first.x + first.w > second.x &&
    first.y < second.y + second.h &&
    first.y + first.h > second.y
  )
}

export function marqueeSelection(rectangle, widgets) {
  return widgets.filter((widget) => rectanglesIntersect(rectangle, widget.layout)).map((widget) => widget.id)
}

export function layoutsCollide(layouts, ignoredIds = []) {
  const ignored = new Set(ignoredIds)
  const candidates = layouts.filter((item) => !ignored.has(item.id))
  return candidates.some((item, index) =>
    candidates.slice(index + 1).some((other) => rectanglesIntersect(item.layout, other.layout)),
  )
}

export function placementCollides(layout, widgets, ignoredIds = []) {
  const ignored = new Set(ignoredIds)
  return widgets.some((widget) => !ignored.has(widget.id) && rectanglesIntersect(layout, widget.layout))
}

export function boundedGroupMove(selected, delta, canvas) {
  if (!selected.length) return { x: 0, y: 0 }
  const left = Math.min(...selected.map((item) => item.layout.x))
  const top = Math.min(...selected.map((item) => item.layout.y))
  const right = Math.max(...selected.map((item) => item.layout.x + item.layout.w))
  const bottom = Math.max(...selected.map((item) => item.layout.y + item.layout.h))
  return {
    x: Math.max(-left, Math.min(canvas.width - right, delta.x)) || 0,
    y: Math.max(-top, Math.min(canvas.height - bottom, delta.y)) || 0,
  }
}

export function alignmentGuides(layout, widgets, threshold = DEFAULT_GRID_SIZE) {
  const movingX = [layout.x, layout.x + layout.w / 2, layout.x + layout.w]
  const movingY = [layout.y, layout.y + layout.h / 2, layout.y + layout.h]
  const targetX = widgets.flatMap((widget) => [
    widget.layout.x,
    widget.layout.x + widget.layout.w / 2,
    widget.layout.x + widget.layout.w,
  ])
  const targetY = widgets.flatMap((widget) => [
    widget.layout.y,
    widget.layout.y + widget.layout.h / 2,
    widget.layout.y + widget.layout.h,
  ])
  const nearest = (moving, targets) =>
    moving
      .flatMap((value) => targets.map((target) => ({ offset: target - value, position: target })))
      .filter((item) => Math.abs(item.offset) <= threshold)
      .sort((first, second) => Math.abs(first.offset) - Math.abs(second.offset))[0]
  const vertical = nearest(movingX, targetX)
  const horizontal = nearest(movingY, targetY)
  return {
    x: layout.x + (vertical?.offset ?? 0),
    y: layout.y + (horizontal?.offset ?? 0),
    vertical: vertical?.position,
    horizontal: horizontal?.position,
  }
}

export function nearestFreePlacement(layout, widgets, canvasWidth, gridSize = DEFAULT_GRID_SIZE) {
  const start = snapLayout(layout, gridSize)
  const maxX = Math.max(0, snapValue(canvasWidth - start.w, gridSize))
  const startX = Math.max(0, Math.min(maxX, start.x))
  const startY = Math.max(0, start.y)
  if (!placementCollides({ ...start, x: startX, y: startY }, widgets)) return { ...start, x: startX, y: startY }
  const columns = Math.floor(maxX / gridSize) + 1
  const maxAttempts = Math.max(columns * 500, 1000)
  for (let attempt = 1; attempt < maxAttempts; attempt += 1) {
    const index = Math.round(startY / gridSize) * columns + Math.round(startX / gridSize) + attempt
    const candidate = { ...start, x: (index % columns) * gridSize, y: Math.floor(index / columns) * gridSize }
    if (!placementCollides(candidate, widgets)) return candidate
  }
  return { ...start, x: startX, y: startY }
}

export function canvasAutoHeight(currentHeight, widgets, padding = 24, gridSize = DEFAULT_GRID_SIZE) {
  const bottom = widgets.reduce((value, widget) => Math.max(value, widget.layout.y + widget.layout.h), 0)
  return Math.max(currentHeight, snapValue(bottom + padding, gridSize))
}

// ── Report page grid (summer 24-column grid, see tokens/layout.js) ──────────────
// Layouts stay in pixels; these helpers convert to cells { col, row, cols, rows }
// and back. A full-bleed header (x≈0, w≈page width) keeps its edge-to-edge band
// and only snaps vertically; a header pinned above the top margin keeps its top.

export function pageGrid(canvas) {
  const width = Number(canvas?.width) || 1123
  const height = Number(canvas?.height) || 794
  return {
    width,
    height,
    cols: GRID_COLS,
    colPitch: (width - GRID_MARGIN * 2 + GRID_GUTTER) / GRID_COLS,
    rowPitch: GRID_ROW_PITCH,
    margin: GRID_MARGIN,
    gutter: GRID_GUTTER,
    // Whole rows that end inside the bottom page margin: 39 portrait, 27 landscape.
    rows: Math.max(1, Math.floor((height - GRID_MARGIN * 2 + GRID_GUTTER) / GRID_ROW_PITCH)),
  }
}

// `edges` rounds both edges to the nearest grid line instead of the position and the
// size separately. Load-time normalisation uses it: an agent layout drawn on its own
// margin (e.g. 32px, content 1059px wide) then still spans the full 24 columns, and
// neighbours that touched keep touching instead of overlapping and being pushed down.
export function gridCells(layout, grid, min = { c: 1, r: 1 }, fullBleed = false, edges = false) {
  const x = finite(layout.x)
  const y = finite(layout.y)
  const w = finite(layout.w, min.c * grid.colPitch - grid.gutter)
  const h = finite(layout.h, min.r * grid.rowPitch - grid.gutter)
  const full = fullBleed && x < grid.margin / 2 && x + w > grid.width - grid.margin / 2
  const startCol = Math.max(0, Math.round((x - grid.margin) / grid.colPitch))
  const spanCols = edges
    ? Math.round((x + w - grid.margin + grid.gutter) / grid.colPitch) - Math.min(startCol, grid.cols - 1)
    : Math.round((w + grid.gutter) / grid.colPitch)
  const cols = full ? grid.cols : Math.min(grid.cols, Math.max(min.c, 1, spanCols))
  const col = full ? 0 : Math.min(grid.cols - cols, startCol)
  const pinned = full && y < grid.margin
  const row = pinned ? 0 : Math.max(0, Math.round((y - grid.margin) / grid.rowPitch))
  // A pinned header keeps its top edge, so its bottom edge snaps instead of its height.
  const spanRows =
    pinned || edges
      ? Math.round((y + h - grid.margin + grid.gutter) / grid.rowPitch) - row
      : Math.round((h + grid.gutter) / grid.rowPitch)
  const rows = Math.max(min.r, 1, spanRows)
  return { col, row, cols, rows, full, top: pinned ? Math.max(0, y) : undefined }
}

export function gridLayout(cells, grid) {
  const top = cells.top ?? grid.margin + cells.row * grid.rowPitch
  const bottom = cells.bottom ?? grid.margin + (cells.row + cells.rows) * grid.rowPitch - grid.gutter
  return {
    x: cells.full ? 0 : Math.round(grid.margin + cells.col * grid.colPitch),
    y: Math.round(top),
    w: cells.full ? grid.width : Math.round(cells.cols * grid.colPitch - grid.gutter),
    h: Math.round(bottom - top),
  }
}

// Snap a pixel layout to whole cells, never below `min` cells.
export function snapGridLayout(layout, grid, min, fullBleed = false) {
  return gridLayout(gridCells(layout, grid, min, fullBleed), grid)
}

// Snap a dragged position to the nearest grid line, keeping the size. A full-bleed
// widget (wider than the grid) stays at x = 0.
export function snapGridPosition(position, layout, grid) {
  const row = Math.max(0, Math.round((finite(position.y) - grid.margin) / grid.rowPitch))
  // HUNT M7: a widget pinned above the top margin (the page-top header, y 0) dragged back to the first row is back on
  // its own top, not on the first row line, so a drag down and back (or a wobble) is no move.
  const pinned = finite(layout.y) < grid.margin - 0.5
  const y = pinned && row === 0 ? Math.round(finite(layout.y)) : Math.round(grid.margin + row * grid.rowPitch)
  if (finite(layout.w) > grid.width - grid.margin / 2) return { x: 0, y }
  const cols = Math.min(grid.cols, Math.max(1, Math.round((finite(layout.w) + grid.gutter) / grid.colPitch)))
  const col = Math.min(grid.cols - cols, Math.max(0, Math.round((finite(position.x) - grid.margin) / grid.colPitch)))
  return { x: Math.round(grid.margin + col * grid.colPitch), y }
}

// First free cell for an already sized `layout`: row-major from its own cell to
// the page end, then from the page top. If the page is full it goes below the
// lowest widget (off-page, under the veil) rather than overlapping a neighbour.
export function nearestFreeGridPlacement(layout, widgets, grid, ignoredIds = []) {
  const ignored = new Set(ignoredIds)
  const others = widgets.filter((widget) => !ignored.has(widget.id) && widget.layout)
  const cells = gridCells(layout, grid)
  const start = gridLayout(cells, grid)
  if (!placementCollides(start, others)) return start
  const columns = grid.cols - cells.cols + 1
  const slots = Math.max(0, grid.rows - cells.rows + 1) * columns
  const first = Math.min(slots, cells.row * columns + Math.min(cells.col, columns - 1))
  const onPage = Array.from({ length: slots }, (_, step) => (first + step) % slots)
    .map((index) => gridLayout({ ...cells, top: undefined, col: index % columns, row: Math.floor(index / columns) }, grid))
    .find((candidate) => !placementCollides(candidate, others))
  if (onPage) return onPage
  const bottom = others.reduce((value, widget) => Math.max(value, widget.layout.y + widget.layout.h), 0)
  const row = Math.max(grid.rows, Math.ceil((bottom + grid.gutter - grid.margin) / grid.rowPitch))
  return gridLayout({ ...cells, top: undefined, col: Math.min(cells.col, columns - 1), row }, grid)
}

// Display-time normalisation of a page whose layout ignores the grid (typically
// agent-written): round every widget to the nearest cells, apply the type's
// minimum footprint, then push overlapping widgets down (reading order: top to
// bottom, left to right) until they are free. Pure: nothing is written back, and
// widgets that did not move keep their identity. Widgets may end up below the
// page bottom; the off-page veil shows them.
export function normalizeLayout(widgets, page) {
  if (!Array.isArray(widgets) || !widgets.length) return widgets || []
  const grid = pageGrid(page)
  const order = widgets
    .map((widget, index) => ({ widget, index }))
    .sort(
      (first, second) =>
        finite(first.widget.layout?.y) - finite(second.widget.layout?.y) ||
        finite(first.widget.layout?.x) - finite(second.widget.layout?.x) ||
        first.index - second.index,
    )
  const placed = []
  const result = [...widgets]
  order.forEach(({ widget, index }) => {
    const layout = widget.layout || {}
    const min = widgetMinCells(widget.type, widget)
    const cells = gridCells(layout, grid, { c: Math.min(grid.cols, min.c), r: min.r }, widget.type === "header", true)
    const hits = () => placed.filter((other) => cellsIntersect(cells, other))
    for (let overlap = hits(); overlap.length; overlap = hits()) {
      cells.row = Math.max(...overlap.map((other) => other.row + other.rows))
      cells.top = undefined
    }
    placed.push(cells)
    const next = gridLayout(cells, grid)
    if (next.x !== layout.x || next.y !== layout.y || next.w !== layout.w || next.h !== layout.h)
      result[index] = { ...widget, layout: { ...layout, ...next } }
  })
  return result
}

// The page-top header (a header on the first rows, full width; "page-top" allows sloppy agent headers a column short):
// the display fit always shows it as the HEADER_ROWS band, so its height is not the user's to drag (HUNT R4).
export function isPageTopHeader(widget, page) {
  if (widget?.type !== "header") return false
  const grid = pageGrid(page)
  return pageTopCells(gridCells(widget.layout || {}, grid, { c: 1, r: 1 }, true, true), grid)
}

function pageTopCells(base, grid) {
  return base.row <= 1 && (base.full || base.cols >= grid.cols - 2)
}

// HUNT R8: the page as Preview shows it and the PDF prints it, one pipeline for both: the saved widgets put on the
// grid (normalizeLayout), then the display fit with the content heights and the measured row stops, squeezed onto the
// paper. Display only; nothing here is saved.
export function printPageLayout(widgets, page, options = {}) {
  return fitPageLayout(normalizeLayout(widgets || [], page), page, { needs: options.needs, stops: options.stops })
}

// ── Display fit (summer "blocks adjust their height") ─────────────────────────────
// Runs on a page that normalizeLayout already put on the grid. Display only: the result is
// what the page shows (and prints); edits keep writing the normalised layout, never this.
//
// 1. Heights from content: a widget whose content was measured (`needs`, px of frame height
//    the content wants) is snapped up to whole rows, between its type minimum and its saved
//    height, each on its own (summer auto height). Widgets without a content height (KPIs,
//    charts) that share a row band keep one height, so a KPI row stays aligned.
//    A page-top header becomes the full-bleed band of HEADER_ROWS.
// 2. Summer compactLayoutVertical: every widget moves straight up into the first free slot,
//    columns unchanged, so the gaps left by shorter widgets close. Widgets saved on one row are
//    a row group and keep one top edge (round 6: no staggered columns); each keeps its height.
// 3. Tables whose content still scrolls, and charts saved below their default footprint, grow
//    one row at a time while the page has free rows, the biggest shortfall first (the widgets
//    below move down with them and must stay on the page).
// 4. A page that still overflows closes its kept gaps, then shrinks back: first to the type
//    minimums, then below them where the content fits, then to FIT_FLOOR_ROWS. Only what is
//    left goes under the veil. `options.squeeze === false` (the report editor, §35) skips the
//    shrink: what is past the page bottom keeps its height and shows whole under the veil
//    (a scrolling table there with nothing under it takes its content height, 5b).
// 5. A table that still scrolls with nothing below it extends to the page bottom margin.
// 6. With measured row stops (`options.stops`), grid rows move between stacked scrolling tables
//    while that shows more whole rows (the table viewport itself ends on a row, wholeRowsHeight).
export function fitPageLayout(widgets, page, options = {}) {
  if (!Array.isArray(widgets) || !widgets.length) return widgets || []
  const grid = pageGrid(page)
  const portrait = grid.height > grid.width
  const needs = options.needs || {}
  const squeeze = options.squeeze !== false
  const items = widgets.map((widget, index) => {
    const layout = widget.layout || {}
    const isHeader = widget.type === "header"
    const base = gridCells(layout, grid, { c: 1, r: 1 }, isHeader, true)
    const minRows = Math.min(grid.rows, widgetMinCells(widget.type, widget).r)
    // "Page-top" allows sloppy agent headers a column short of the full width.
    const topHeader = isHeader && pageTopCells(base, grid)
    // §36: a widget whose height the user dragged (`layout.autoHeight === false`) keeps it: no content fit, no growth.
    // In the editor (squeeze: false) it is also anchored at its saved row, so no display pass moves it.
    const sized = !topHeader && layout.autoHeight === false
    const needPx = Number(needs[widget.id])
    const need =
      !sized && CONTENT_FIT_TYPES.includes(widget.type) && needPx > 0
        ? Math.max(1, Math.ceil((needPx + grid.gutter) / grid.rowPitch))
        : undefined
    const floor = FIT_FLOOR_ROWS[widget.type] ?? DEFAULT_FIT_FLOOR_ROWS
    let rows = base.rows
    // The page-top header is always the token band, whatever height was saved: any edit writes
    // the normalised layout back, so a saved height says nothing about intent.
    if (topHeader) rows = HEADER_ROWS[portrait ? "portrait" : "landscape"]
    // Content height (summer auto height): down to the fit floor when the content is that short
    // (a 1-row table is not held at the 6-row editing minimum), never above the saved height.
    else if (need !== undefined) rows = Math.max(Math.min(floor, minRows), Math.min(base.rows, need))
    return {
      index,
      widget,
      layout,
      base,
      topHeader,
      sized,
      anchor: sized && !squeeze,
      need,
      minRows,
      floor: Math.min(floor, minRows),
      // Growth target: measured content for tables, the default footprint for charts.
      want:
        need !== undefined && CONTENT_GROW_TYPES.includes(widget.type)
          ? need
          : !sized && FOOTPRINT_GROW_TYPES.includes(widget.type)
            ? Math.min(grid.rows, widgetCells(widget.type).r)
            : undefined,
      cells: topHeader ? { col: 0, cols: grid.cols, row: 0, rows, full: true, top: 0 } : { ...base, top: undefined, rows },
    }
  })
  // Row bands: widgets that started on the same row with the same height move as one, but only
  // widgets without a measured content height (KPIs, charts). A table or text block keeps its own
  // content height (summer: auto height per widget, then vertical compaction), so a 1-row table
  // next to a long one is not stretched to match it; the free rows under it stay grid space.
  const bands = []
  items.forEach((item) => {
    if (item.topHeader || item.need !== undefined || item.sized) return bands.push([item])
    const band = bands.find(
      (members) =>
        !members[0].topHeader &&
        members[0].need === undefined &&
        members[0].base.row === item.base.row &&
        members[0].base.rows === item.base.rows,
    )
    if (band) band.push(item)
    else bands.push([item])
  })
  bands.forEach((band) => {
    const rows = Math.max(...band.map((item) => item.cells.rows))
    band.forEach((item) => (item.cells.rows = rows))
  })
  const order = [...items].sort(
    (first, second) =>
      Number(second.topHeader) - Number(first.topHeader) ||
      first.base.row - second.base.row ||
      first.base.col - second.base.col ||
      first.index - second.index,
  )
  // A gap above a widget in the saved (normalised) layout is whitespace the author meant and is kept, one row too
  // (HUNT R5, R10, M11: a widget moved or a bottom edge shrunk by one row stays where the user put it, in the editor
  // and in Preview alike). normalizeLayout's edge rounding already makes agent neighbours that touched keep touching.
  // Only the space a content fit frees (a table shorter than saved) closes; Preview's step 4 closes kept gaps first.
  order.forEach((item, position) => {
    const above = order.slice(0, position).filter((other) => colsOverlap(other.base, item.base) && other.base.row < item.base.row)
    const bottom = above.reduce((value, other) => Math.max(value, other.base.row + other.base.rows), 0)
    const gap = item.base.row - bottom
    item.keepGap = gap > 0 ? gap : 0
  })
  // Row groups: widgets that started on the same saved row with the same saved height keep one top edge (no staggered
  // "лесенка" when one column compacts further than the other). Each card keeps its own height; the group is placed
  // as a unit at the lowest top any member needs, then the next group follows. Cards of different heights that happen
  // to share a row are separate columns (HUNT M11: a push that lands a table on its neighbour column's row never pulls
  // that column down).
  const groups = []
  const anchors = items.filter((item) => item.anchor)
  order.forEach((item) => {
    const group =
      !item.topHeader &&
      !item.anchor &&
      groups.find(
        (members) =>
          !members[0].topHeader && !members[0].anchor && members[0].base.row === item.base.row && members[0].base.rows === item.base.rows,
      )
    if (group) group.push(item)
    else groups.push([item])
  })
  const compact = () => {
    const placed = []
    const lowestFree = (cells, row) => {
      cells.row = row
      for (let moved = true; moved; ) {
        moved = false
        placed.forEach((other) => {
          if (cellsIntersect(cells, other) && other.row + other.rows > cells.row) {
            cells.row = other.row + other.rows
            moved = true
          }
        })
      }
      return cells.row
    }
    groups.forEach((group) => {
      if (group[0].topHeader) return group.forEach((item) => placed.push(item.cells))
      // Straight up (summer compaction): a widget saved below another in its columns stays below it, never jumps over it
      // into a hole above it (HUNT M3: l2 pushed under a dropped l1 is shown under it, as the drag showed).
      const floorRow = (item) =>
        items.reduce(
          (value, other) =>
            other !== item && !other.topHeader && colsOverlap(other.cells, item.cells) && other.base.row < item.base.row
              ? Math.max(value, other.cells.row + other.cells.rows)
              : value,
          0,
        )
      const start = (item) =>
        Math.max(
          floorRow(item),
          item.keepGap
            ? placed.reduce(
                (value, other) => (colsOverlap(other, item.cells) ? Math.max(value, other.row + other.rows) : value),
                0,
              ) + item.keepGap
            : 0,
        )
      let top = Math.max(...group.map((item) => lowestFree(item.cells, start(item))))
      // A member pushed down to the shared top may now hit a widget lower in its columns.
      for (let guard = 0; guard < grid.rows * 4; guard += 1) {
        const next = Math.max(...group.map((item) => lowestFree(item.cells, top)))
        if (next === top) break
        top = next
      }
      group.forEach((item) => {
        item.cells.row = top
        placed.push(item.cells)
      })
    })
  }
  const overflow = () => items.reduce((sum, item) => sum + Math.max(0, item.cells.row + item.cells.rows - grid.rows), 0)
  const setBand = (band, rows) => band.forEach((item) => (item.cells.rows = rows))
  const bandRows = (band) => band[0].cells.rows
  compact()
  // §36: no display pass after the compaction moves an anchored widget (growth above it is refused, not pushed into it).
  const anchorRows = anchors.map((item) => item.cells.row)
  const respectsAnchors = () => anchors.every((item, index) => item.cells.row === anchorRows[index])
  // §36: in the editor (squeeze: false) a widget past the page bottom in one column must not switch growth off in the
  // others: growth and the step-5 slack are refused only when they add overflow, not whenever the page has some.
  const allowed = squeeze ? 0 : overflow()
  // 3. Grow scrolling tables (and short charts) into the free rows below them, the biggest shortfall first.
  if (overflow() <= allowed) {
    for (let step = 0; step < grid.rows * bands.length; step += 1) {
      const candidates = bands
        .filter((band) => band.some((item) => item.want > item.cells.rows))
        .sort((first, second) => shortfall(second) - shortfall(first))
      const accepted = candidates.find((band) => {
        const rows = bandRows(band)
        setBand(band, rows + 1)
        compact()
        if (overflow() <= allowed && respectsAnchors()) return true
        setBand(band, rows)
        compact()
        return false
      })
      if (!accepted) break
    }
  }
  // 4. Fit an overflowing page before the veil: first close the whitespace that was kept,
  //    then shrink widgets tier by tier.
  // In the editor (squeeze: false, §35) what passes the bottom stays where the user put it: the kept gaps stay too.
  for (let guard = 0; squeeze && overflow() > 0 && guard < 500; guard += 1) {
    const before = overflow()
    const gapped = order.filter((item) => item.keepGap > 0 && !item.anchor).sort((first, second) => second.keepGap - first.keepGap)
    const best = gapped.find((item) => {
      item.keepGap -= 1
      compact()
      if (overflow() < before) return true
      item.keepGap += 1
      return false
    })
    compact()
    if (!best) break
  }
  const floors = [
    (item) => item.minRows,
    (item) => (item.need !== undefined && item.need < item.minRows ? Math.max(item.floor, item.need) : item.minRows),
    (item) => item.floor,
  ]
  for (let tier = 0, guard = 0; squeeze && tier < floors.length && overflow() > 0 && guard < 500; guard += 1) {
    const before = overflow()
    const floor = floors[tier]
    let best
    bands.forEach((band) => {
      const rows = bandRows(band)
      if (band[0].topHeader || band.some((item) => rows - 1 < floor(item))) return
      setBand(band, rows - 1)
      compact()
      const after = overflow()
      // Prefer a widget that still shows all its content, then the taller one.
      // A KPI's content need is its type minimum (label plus a readable value).
      const fits = band.every((item) => {
        const need = item.need ?? (item.widget.type === "kpi" ? item.minRows : undefined)
        return need === undefined || need <= rows - 1
      })
      const score = [before - after, Number(fits), rows]
      if (score[0] > 0 && (!best || compareScore(score, best.score) > 0)) best = { band, score }
      setBand(band, rows)
    })
    if (best) setBand(best.band, bandRows(best.band) - 1)
    else tier += 1
    compact()
  }
  // 5. The page bottom margin is below the last whole grid row (landscape: rows end at 756, the
  //    margin at 778). A table that still scrolls with nothing under it takes that slack too.
  const pageBottom = grid.height - grid.margin
  const applySlack = () => {
    items.forEach((item) => (item.cells.bottom = undefined))
    if (squeeze && overflow() > 0) return
    items.forEach((item) => {
      if (item.topHeader || !CONTENT_GROW_TYPES.includes(item.widget.type) || !(item.need > item.cells.rows)) return
      if (item.cells.row + item.cells.rows > grid.rows) return
      const below = items.some(
        (other) => other !== item && colsOverlap(other.cells, item.cells) && other.cells.row >= item.cells.row + item.cells.rows,
      )
      if (!below) item.cells.bottom = pageBottom
    })
  }
  applySlack()
  // 5b. Editor only (squeeze: false, §35, summer auto height): a scrolling table that already passes the page bottom
  //     with nothing under it takes its content height, so all its rows show under the veil.
  if (!squeeze && overflow() > 0) {
    items.forEach((item) => {
      const { cells } = item
      if (item.topHeader || !CONTENT_GROW_TYPES.includes(item.widget.type) || !(item.need > cells.rows)) return
      if (cells.row + cells.rows <= grid.rows) return
      const below = items.some((other) => other !== item && colsOverlap(other.cells, cells) && other.cells.row >= cells.row + cells.rows)
      if (!below) cells.rows = item.need
    })
  }
  // 6. Whole rows (round 6): a scrolling table shows only whole rows, so a frame between two row
  //    stops leaves blank space in the card. Trade one grid row between stacked scrolling tables
  //    while that shows more whole rows in total (or as many with less blank space).
  const stops = options.stops || {}
  const rowScore = () =>
    items.reduce(
      (score, item) => {
        const list = stops[item.widget.id]
        if (!Array.isArray(list) || !list.length) return score
        const h = gridLayout(item.cells, grid).h
        // A table that fits shows all its rows; one that scrolls shows the stops under its height.
        if (!(item.need > item.cells.rows)) return [score[0] + list.length, score[1]]
        const shown = list.filter((stop) => stop <= h)
        const blank = h - (shown.length ? shown[shown.length - 1] : h)
        return [score[0] + shown.length, score[1] - blank]
      },
      [0, 0],
    )
  // HUNT R11: Preview and print only (squeeze). In the editor a trade reflows the stacked tables whenever one of them
  // changes: a top-edge drag of Ismail's off-page Bottom P&L (a manual height, so no 5b) switched it on and grew
  // Performance and cut Top holdings above it; a table resized by hand leaves a trade it was in, and its neighbour
  // jumps. The editor shows each table at its own fitted height; Preview still trades rows for whole rows on paper.
  if (squeeze && overflow() <= allowed && Object.keys(stops).length) {
    for (let guard = 0; guard < grid.rows; guard += 1) {
      const before = rowScore()
      let best
      bands.forEach((band) => {
        const rows = bandRows(band)
        // Only a band of scrolling tables gives a row: a table that shows all its rows keeps them.
        if (
          band[0].topHeader ||
          band.some((item) => rows - 1 < item.floor || !Array.isArray(stops[item.widget.id]) || !(item.need > rows - 1))
        )
          return
        setBand(band, rows - 1)
        compact()
        // The freed row goes to the scrolling tables below: grow each band that can take it.
        const grown = bands.filter((other) => {
          if (other === band || !other.some((item) => item.want > item.cells.rows)) return false
          const otherRows = bandRows(other)
          setBand(other, otherRows + 1)
          compact()
          if (overflow() <= allowed && respectsAnchors()) return true
          setBand(other, otherRows)
          compact()
          return false
        })
        applySlack()
        const score = rowScore()
        if (grown.length && overflow() <= allowed && compareScore(score, before) > 0 && (!best || compareScore(score, best.score) > 0))
          best = { band, grown, score }
        grown.forEach((other) => setBand(other, bandRows(other) - 1))
        setBand(band, rows)
        compact()
        applySlack()
      })
      if (!best) break
      setBand(best.band, bandRows(best.band) - 1)
      best.grown.forEach((other) => setBand(other, bandRows(other) + 1))
      compact()
      applySlack()
    }
  }
  return items.map((item) => {
    const next = gridLayout(item.cells, grid)
    return sameLayout(next, item.layout) ? item.widget : { ...item.widget, layout: { ...item.layout, ...next } }
  })
}

function colsOverlap(first, second) {
  return first.col < second.col + second.cols && first.col + first.cols > second.col
}

function shortfall(band) {
  return Math.max(...band.map((item) => (item.want !== undefined ? item.want - item.cells.rows : 0)))
}

function compareScore(first, second) {
  for (let index = 0; index < first.length; index += 1) if (first[index] !== second[index]) return first[index] - second[index]
  return 0
}

function sameLayout(first, second) {
  return (
    Number(first?.x) === Number(second?.x) &&
    Number(first?.y) === Number(second?.y) &&
    Number(first?.w) === Number(second?.w) &&
    Number(first?.h) === Number(second?.h)
  )
}

// ── Widgets yield while dragging (summer auto-grid, Artyom 14cdf9f55) ─────────────────────────
// Summer `pushAwayCollisions` + `applyMoveWithPush` during the drag and `compactLayoutVertical`
// on drop. Both work on a layout map { id: { x, y, w, h } } in page pixels on the report grid.

// The first grid row line at or below `bottom` plus a gutter: where a pushed widget's top goes.
export function rowLineAfter(bottom, grid) {
  const row = Math.max(0, Math.ceil((bottom + grid.gutter - grid.margin) / grid.rowPitch - 1e-6))
  return grid.margin + row * grid.rowPitch
}

function bareRect(rect) {
  return { x: finite(rect?.x), y: finite(rect?.y), w: finite(rect?.w), h: finite(rect?.h) }
}

// Live push for a drag or a resize. `id` is the dragged widget (or an array of ids for a group
// drag) and `ghostRect` its grid-snapped drop slot (for a group, the members already sit at their
// slots in `layout` and `ghostRect` is the group's bounding box). Every other widget the ghost
// overlaps moves straight down to the row under it, and the pushes cascade (summer
// pushAwayCollisions). A displaced widget that ends past the page bottom goes to the first free
// slot on the page; with none it stays below, under the off-page veil. Pure: always computed from
// the layout at drag start, so a widget the ghost has left returns to its place.
/** @returns {Record<string, { x: number, y: number, w: number, h: number }>} */
export function pushLayoutForDrag(layout, id, ghostRect, page, options = {}) {
  const grid = pageGrid(page)
  const moving = new Set((Array.isArray(id) ? id : [id]).map(String))
  const fixed = new Set((options.fixed || []).map(String))
  /** @type {Record<string, { x: number, y: number, w: number, h: number }>} */
  const items = {}
  Object.entries(layout || {}).forEach(([key, rect]) => {
    if (rect) items[key] = { ...rect }
  })
  const ghost = bareRect(ghostRect)
  if (!Array.isArray(id) && items[id]) items[id] = { ...items[id], ...ghost }
  const order = Object.keys(items)
    .filter((key) => !moving.has(key))
    .sort((first, second) => items[first].y - items[second].y || items[first].x - items[second].x)
  // HUNT M6: a group pushes from each member's own slot, never from the group's bounding box.
  const queue = Array.isArray(id) ? [...moving].map((key) => items[key]).filter(Boolean) : [ghost]
  if (!queue.length) queue.push(ghost)
  for (let guard = 0; queue.length && guard < 4000; guard += 1) {
    const anchor = queue.shift()
    order.forEach((key) => {
      const item = items[key]
      // HUNT M8 / P7: the header pinned above the top margin, and `options.fixed` (the page-top header), are never pushed.
      if (item === anchor || item.y < grid.margin - 0.5 || fixed.has(key) || !rectanglesIntersect(anchor, item)) return
      const y = rowLineAfter(anchor.y + anchor.h, grid)
      if (y > item.y) {
        item.y = y
        queue.push(item)
      }
    })
  }
  // §36: a resize pushes straight down only; a pushed widget never jumps to a free slot in another column.
  if (options.relocate === false) return items
  // The page's row budget (the last whole row ends at 756 landscape), as the display fit counts it.
  const pageBottom = grid.margin + grid.rows * grid.rowPitch - grid.gutter + 0.5
  order.forEach((key) => {
    const item = items[key]
    if (item.y === layout[key].y || item.y + item.h <= pageBottom) return
    const others = Object.keys(items)
      .filter((other) => other !== key)
      .map((other) => ({ id: other, layout: items[other] }))
    // Reading order from the page top (from its own column), so the search never returns the
    // off-page spot itself.
    const slot = nearestFreeGridPlacement({ ...item, y: grid.margin }, others, grid)
    if (slot.y + slot.h <= pageBottom && !placementCollides(slot, [{ id: "ghost", layout: ghost }]))
      items[key] = { ...item, ...slot }
  })
  return items
}

// Compaction on drop (summer compactLayoutVertical): every widget moves straight up into the
// first free slot, columns unchanged. Widgets saved on one row are a row group and keep one top
// (round 6). `options.fixed` (the dropped or resized widgets) and a header pinned above the top
// margin stay where they are; the others may move up past them (a widget dragged down over its
// neighbour swaps with it instead of snapping back, which summer's plain compaction did).
/** @returns {Record<string, { x: number, y: number, w: number, h: number }>} */
export function compactLayoutOnDrop(layout, page, options = {}) {
  const grid = pageGrid(page)
  const fixed = new Set((options.fixed || []).map(String))
  /** @type {Record<string, { x: number, y: number, w: number, h: number }>} */
  const items = {}
  Object.entries(layout || {}).forEach(([key, rect]) => {
    if (rect) items[key] = { ...rect }
  })
  const placed = []
  const free = []
  Object.keys(items).forEach((key) => (fixed.has(key) || items[key].y < grid.margin - 0.5 ? placed : free).push(key))
  placed.forEach((key, index) => (placed[index] = items[key]))
  free.sort((first, second) => items[first].y - items[second].y || items[first].x - items[second].x)
  const groups = []
  free.forEach((key) => {
    const group = groups.find((members) => Math.abs(items[members[0]].y - items[key].y) < 0.5)
    if (group) group.push(key)
    else groups.push([key])
  })
  const lowestFree = (rect, start) => {
    let y = start
    for (let moved = true, guard = 0; moved && guard < 1000; guard += 1) {
      moved = false
      placed.forEach((other) => {
        const below = rowLineAfter(other.y + other.h, grid)
        if (below > y && rectanglesIntersect({ ...rect, y }, other)) {
          y = below
          moved = true
        }
      })
    }
    return y
  }
  groups.forEach((group) => {
    let top = Math.max(...group.map((key) => lowestFree(items[key], grid.margin)))
    for (let guard = 0; guard < 200; guard += 1) {
      const next = Math.max(...group.map((key) => lowestFree(items[key], top)))
      if (next === top) break
      top = next
    }
    group.forEach((key) => {
      items[key].y = top
      placed.push(items[key])
    })
  })
  return items
}

// A displayed rect as it is saved after a drop: whole rows. The display fit's page-bottom slack
// (a table stretched to 778) is cut back to the last whole row; the fit adds it again.
export function gridRowRect(rect, grid) {
  const pinned = rect.y < grid.margin - 0.5
  const top = pinned ? rect.y : grid.margin + Math.max(0, Math.round((rect.y - grid.margin) / grid.rowPitch)) * grid.rowPitch
  const rows = Math.floor((rect.y + rect.h - grid.margin + grid.gutter) / grid.rowPitch + 1e-6)
  const bottom = Math.max(top + grid.rowPitch - grid.gutter, grid.margin + rows * grid.rowPitch - grid.gutter)
  return { ...rect, y: Math.round(top), h: Math.round(bottom - top) }
}

// What a drop or resize saves. The display fit is display-only, so the settled flow (computed on the
// displayed, content-fitted rects the user saw) gives every widget its position in whole rows, but
// each keeps its saved height, except the `resized` widgets, which take the rect the user dragged.
// Saved heights can be taller than the fitted ones, so the page is then compacted on the saved
// heights, in the flow's reading order: each widget moves down just past any widget placed before
// it that it would overlap. Nothing moves up, columns never change, and the result never overlaps.
/** @returns {Record<string, { x: number, y: number, w: number, h: number }>} */
export function persistFlowLayout(settled, saved, page, options = {}) {
  const grid = pageGrid(page)
  const resized = new Set((options.resized || []).map(String))
  // `options.fixed` (a library drop's new widget, §32) is placed first and never moves; the others flow around it.
  const fixed = new Set((options.fixed || []).map(String))
  // §36 `options.anchor` (a widget whose height was resized): placed first, never moves; a widget that starts above it
  // and whose saved height reaches into it is cut to end above it (it was shown above it, so nothing it showed is lost);
  // one that would keep less than a row goes under it.
  const anchor = new Set((options.anchor || []).map(String))
  /** @type {Record<string, { x: number, y: number, w: number, h: number }>} */
  const items = {}
  Object.entries(settled || {}).forEach(([key, rect]) => {
    if (!rect) return
    const slot = gridRowRect(rect, grid)
    const own = saved?.[key]
    items[key] = resized.has(key) || !own ? slot : { ...slot, h: own.h }
  })
  const rank = (key) => (anchor.has(key) ? 2 : fixed.has(key) ? 1 : 0)
  const order = Object.keys(items).sort(
    (first, second) => rank(second) - rank(first) || items[first].y - items[second].y || items[first].x - items[second].x,
  )
  const placed = []
  const anchored = new Set()
  order.forEach((key) => {
    const item = items[key]
    for (let moved = true, guard = 0; moved && guard < 1000; guard += 1) {
      moved = false
      placed.forEach((other) => {
        if (!rectanglesIntersect(item, other)) return
        // Review (§40, 3): a cut that would leave less than one row (a widget pinned less than a row above the anchor)
        // could not clear it and spun the loop 1000 times with the overlap saved; such a widget goes under it instead.
        const cut = other.y - grid.gutter - item.y
        if (anchored.has(other) && item.y < other.y && cut >= grid.rowPitch - grid.gutter - 0.5) item.h = cut
        else item.y = rowLineAfter(other.y + other.h, grid)
        moved = true
      })
    }
    placed.push(item)
    if (anchor.has(key)) anchored.add(item)
  })
  return items
}

// §36 A resize on the report grid (summer resizeLayoutWithGuides): only the dragged edges move, each to the nearest
// grid line; the other edges stay exactly where the widget was shown (one past the page bottom is not pulled onto the
// page, as constrainReportGridLayout would). Never below `min` cells. The top edge stops at the widgets above it in
// its columns (summer: collide and stop); the side edges stay inside the columns.
/** @returns {{ x: number, y: number, w: number, h: number }} */
export function gridResizeLayout(start, raw, direction, page, min = { c: 1, r: 1 }, others = []) {
  const grid = pageGrid(page)
  const from = bareRect(start)
  const to = bareRect(raw)
  const dir = String(direction || "")
  const moved = (first, second) => Math.abs(first - second) > 0.5
  const edges = {
    left: /left/i.test(dir) && moved(to.x, from.x),
    right: /right/i.test(dir) && moved(to.x + to.w, from.x + from.w),
    top: /^top/i.test(dir) && moved(to.y, from.y),
    bottom: /^bottom/i.test(dir) && moved(to.y + to.h, from.y + from.h),
  }
  const line = (value) => Math.round((value - grid.margin) / grid.colPitch)
  const end = (value) => Math.round((value - grid.margin + grid.gutter) / grid.colPitch)
  const rowLine = (value) => Math.round((value - grid.margin) / grid.rowPitch)
  const rowEnd = (value) => Math.round((value - grid.margin + grid.gutter) / grid.rowPitch)
  let left = from.x
  let right = from.x + from.w
  let top = from.y
  let bottom = from.y + from.h
  // HUNT R1 (summer resizeCollisionCandidates): a side edge collides with the widget beside it (one whose rows overlap
  // the widget's) and stops at its column line; a neighbour in another column never moves.
  const beside = (others || []).map((other) => bareRect(other?.layout || other)).filter((rect) => rect.y < from.y + from.h - 0.5 && rect.y + rect.h > from.y + 0.5)
  const leftWall = beside.reduce((value, rect) => (rect.x + rect.w <= from.x + 0.5 ? Math.max(value, end(rect.x + rect.w)) : value), 0)
  const rightWall = beside.reduce((value, rect) => (rect.x >= from.x + from.w - 0.5 ? Math.min(value, line(rect.x)) : value), grid.cols)
  if (edges.left) left = Math.round(grid.margin + Math.max(0, leftWall, Math.min(line(to.x), end(right) - min.c)) * grid.colPitch)
  if (edges.right) right = Math.round(grid.margin + Math.min(grid.cols, rightWall, Math.max(end(to.x + to.w), Math.max(0, line(left)) + min.c)) * grid.colPitch - grid.gutter)
  if (edges.top) {
    const ceiling = (others || []).reduce((value, other) => {
      const rect = bareRect(other?.layout || other)
      const above = rect.x < right && rect.x + rect.w > left && rect.y + rect.h <= from.y + 0.5
      return above ? Math.max(value, rowEnd(rect.y + rect.h)) : value
    }, 0)
    top = Math.round(grid.margin + Math.max(ceiling, Math.min(rowLine(to.y), rowEnd(bottom) - min.r)) * grid.rowPitch)
  }
  if (edges.bottom) bottom = Math.round(grid.margin + Math.max(rowEnd(to.y + to.h), Math.max(0, rowLine(top)) + min.r) * grid.rowPitch - grid.gutter)
  return { x: left, y: top, w: right - left, h: bottom - top }
}

// HUNT M8 / P7: a moved or dropped widget never lands on the page-top header (isPageTopHeader): a slot that overlaps
// it goes to the first row under its band. `headers` are the header rects as shown.
/** @returns {{ x: number, y: number, w: number, h: number }} */
export function clearOfHeaders(rect, headers, page) {
  const grid = pageGrid(page)
  const slot = { ...rect }
  ;(headers || []).forEach((other) => {
    if (other && rectanglesIntersect(slot, bareRect(other))) slot.y = rowLineAfter(finite(other.y) + finite(other.h), grid)
  })
  return slot
}

// HUNT P2: after a delete, only the deleted widgets' own columns close up. A widget below a deleted one whose columns
// overlap it moves straight up into the freed rows (never above the deleted widget's top), and the widgets below it in
// its columns follow; every other widget keeps its row and the gaps the user left. Unmoved widgets keep their identity.
/** @param {any[]} widgets @param {any[]} deleted @param {any} page @returns {any[]} */
export function closeDeletedGap(widgets, deleted, page) {
  if (!Array.isArray(widgets) || !widgets.length || !deleted?.length) return widgets || []
  const grid = pageGrid(page)
  const items = widgets.map((widget) => ({ widget, rect: bareRect(widget.layout) }))
  const holes = deleted.filter((widget) => widget?.layout).map((widget) => bareRect(widget.layout))
  const cols = (a, b) => a.x < b.x + b.w - 0.5 && a.x + a.w > b.x + 0.5
  // The widgets that may move: below a hole in its columns, then (in reading order) below a widget that may move.
  const free = new Map()
  const order = [...items].sort((a, b) => a.rect.y - b.rect.y || a.rect.x - b.rect.x)
  order.forEach((item) => {
    if (item.rect.y < grid.margin - 0.5) return
    const floors = [
      ...holes.filter((hole) => cols(hole, item.rect) && hole.y <= item.rect.y + 0.5).map((hole) => hole.y),
      ...[...free.entries()].filter(([other]) => cols(other.rect, item.rect) && other.rect.y < item.rect.y).map(([, floor]) => floor),
    ]
    if (floors.length) free.set(item, Math.min(...floors))
  })
  if (!free.size) return widgets
  const placed = items.filter((item) => !free.has(item)).map((item) => item.rect)
  order.forEach((item) => {
    if (!free.has(item)) return
    const rect = item.rect
    let y = Math.max(grid.margin, free.get(item))
    for (let moved = true, guard = 0; moved && guard < 1000; guard += 1) {
      moved = false
      placed.forEach((other) => {
        const below = rowLineAfter(other.y + other.h, grid)
        if (below > y && rectanglesIntersect({ ...rect, y }, other)) {
          y = below
          moved = true
        }
      })
    }
    item.rect = { ...rect, y: Math.min(rect.y, y) }
    placed.push(item.rect)
  })
  return items.map(({ widget, rect }) => (Math.abs(rect.y - finite(widget.layout?.y)) < 0.5 ? widget : { ...widget, layout: { ...widget.layout, y: Math.round(rect.y) } }))
}

// Default footprint of a new widget of `type`, at the cell nearest to `position`.
export function defaultGridLayout(type, position, grid) {
  const cells = widgetCells(type)
  const cols = Math.min(grid.cols, cells.c)
  const col = Math.min(grid.cols - cols, Math.max(0, Math.round((finite(position?.x, grid.margin) - grid.margin) / grid.colPitch)))
  const row = Math.max(0, Math.round((finite(position?.y, grid.margin) - grid.margin) / grid.rowPitch))
  return gridLayout({ col, row, cols, rows: cells.r }, grid)
}

// Where a library element dropped at `point` (page px) lands (V2 DESIGN-LOG §31): the type's default footprint (the size
// addWidget uses), centred on the pointer, snapped to whole cells and clamped inside the page's columns and row budget.
// The drag ghost and the drop both use it, so the slot shown and the widget saved never disagree.
export function dropSlotLayout(type, point, page) {
  const grid = pageGrid(page)
  const cells = widgetCells(type)
  const cols = Math.min(grid.cols, cells.c)
  const rows = Math.max(1, cells.r)
  const left = finite(point?.x, grid.margin) - (cols * grid.colPitch - grid.gutter) / 2
  const top = finite(point?.y, grid.margin) - (rows * grid.rowPitch - grid.gutter) / 2
  const col = Math.min(grid.cols - cols, Math.max(0, Math.round((left - grid.margin) / grid.colPitch)))
  const row = Math.min(Math.max(0, grid.rows - rows), Math.max(0, Math.round((top - grid.margin) / grid.rowPitch)))
  return gridLayout({ col, row, cols, rows }, grid)
}

// ── Library drop: fit first, then push (V2 DESIGN-LOG §32) ───────────────────────────────────────
// Saved heights can be taller than what the display fit shows, so a page can read full with visible room. These helpers
// give each widget its content-fit height (the display fit's arithmetic, but never below the type minimum), plan a drop
// on those heights, and move what still does not fit to the top of the next page.

// The saved layouts with every measured table and text block at its content-fit height: `needs` (px of frame height the
// content wants, as fitPageLayout gets it) snapped up to whole rows, between the type minimum and the saved height.
// Widgets without a measurement keep their saved rect. Pure; `shrunk` lists the widgets whose height went down.
// §34: the fit only removes empty space, it never hides content. A table is fitted only with its whole-row stops
// (`stops[id]`, the frame height at which each body row shows, widgetContentFit), and never below its last row's stop,
// whatever the measured need says; without stops (rows not measured yet, no data, a free table) it keeps its saved height.
/** @returns {{ layouts: Record<string, { x: number, y: number, w: number, h: number }>, shrunk: string[] }} */
export function contentFitLayouts(widgets, page, needs = {}, stops = {}) {
  const grid = pageGrid(page)
  /** @type {Record<string, { x: number, y: number, w: number, h: number }>} */
  const layouts = {}
  const shrunk = []
  ;(widgets || []).forEach((widget) => {
    if (!widget?.layout) return
    const layout = bareRect(widget.layout)
    layouts[widget.id] = layout
    const needPx = Number(needs?.[widget.id])
    if (!CONTENT_FIT_TYPES.includes(widget.type) || !(needPx > 0) || widget.layout.autoHeight === false) return
    const rowStops = (stops?.[widget.id] || []).map(Number).filter((value) => value > 0)
    if (widget.type !== "text" && !rowStops.length) return
    const natural = Math.max(needPx, ...rowStops)
    const rows = Math.max(1, Math.round((layout.h + grid.gutter) / grid.rowPitch))
    const need = Math.max(1, Math.ceil((natural + grid.gutter) / grid.rowPitch))
    const fit = Math.max(Math.min(grid.rows, widgetMinCells(widget.type, widget).r), Math.min(rows, need))
    const h = fit * grid.rowPitch - grid.gutter
    if (h < layout.h - 0.5) {
      layouts[widget.id] = { ...layout, h }
      shrunk.push(widget.id)
    }
  })
  return { layouts, shrunk }
}

// The widgets of `layouts` whose bottom passes the page's row budget (the last whole row), in reading order.
export function gridOverflowIds(layouts, page, ignoredIds = []) {
  const grid = pageGrid(page)
  const ignored = new Set(ignoredIds.map(String))
  const pageBottom = grid.margin + grid.rows * grid.rowPitch - grid.gutter + 0.5
  return Object.keys(layouts || {})
    .filter((key) => !ignored.has(key) && layouts[key] && layouts[key].y + layouts[key].h > pageBottom)
    .sort((first, second) => layouts[first].y - layouts[second].y || layouts[first].x - layouts[second].x)
}

// A library element dropped in `slot` as `id`. `shown` is the page as displayed (the push runs on what the user sees),
// `saved` the saved layouts, `fitted` the content-fit ones (contentFitLayouts). The drop's flow is a moved widget's
// (summer push, compaction around the new widget, persistFlowLayout). The fit comes first: if the flow on the saved
// heights leaves something past the page bottom, it runs again on the fitted heights. Whatever still overflows is listed
// in `overflow` (reading order) for the next page; `settled` is the flow on the shown rects. Only the drop's own overflow counts: a widget that is past the page
// bottom on those heights without the drop too (the display fit keeps it on the page today) stays. `fitted` says which
// heights were used.
export function planGridDrop(shown, saved, fitted, id, slot, page, options = {}) {
  // `options.fixed`: widgets the drop never pushes (the page-top header, HUNT P7).
  const settled = compactLayoutOnDrop(pushLayoutForDrag({ ...shown, [id]: slot }, id, slot, page, { fixed: options.fixed }), page, { fixed: [id, ...(options.fixed || [])] })
  const plan = (heights) => {
    const layouts = persistFlowLayout(settled, heights, page, { fixed: [id] })
    const before = new Set(gridOverflowIds(persistFlowLayout(shown, heights, page), page))
    return { layouts, overflow: gridOverflowIds(layouts, page, [id]).filter((key) => !before.has(key)) }
  }
  const first = plan(saved)
  if (!first.overflow.length || !fitted) return { ...first, settled, fitted: false }
  return { ...plan(fitted), settled, fitted: true }
}

// The widgets pushed off a page (`moved`, their rects there) at the top of the next page, in their order and their
// relative layout: the group's top goes to the first row (under a page-top header), and that page's own widgets move
// down by the group's height, so nothing overlaps. Pure; `others` holds only the rects that changed.
export function moveToPageTop(moved, targetWidgets, page) {
  const grid = pageGrid(page)
  const keys = Object.keys(moved || {}).filter((key) => moved[key])
  /** @type {Record<string, { x: number, y: number, w: number, h: number }>} */
  const placed = {}
  /** @type {Record<string, { x: number, y: number, w: number, h: number }>} */
  const others = {}
  if (!keys.length) return { moved: placed, others }
  // Review (§40, 9): only the page-top header (full width, first rows) holds the top; a narrow header in the first row
  // is an ordinary widget and moves down with the others.
  const header = (targetWidgets || []).filter((widget) => widget.layout && isPageTopHeader(widget, page))
  const top = header.length
    ? rowLineAfter(Math.max(...header.map((widget) => finite(widget.layout.y) + finite(widget.layout.h))), grid)
    : grid.margin
  const minY = Math.min(...keys.map((key) => moved[key].y))
  keys.forEach((key) => (placed[key] = { ...bareRect(moved[key]), y: Math.round(moved[key].y - minY + top) }))
  const bottom = Math.max(...keys.map((key) => placed[key].y + placed[key].h))
  const shift = rowLineAfter(bottom, grid) - top
  ;(targetWidgets || []).forEach((widget) => {
    if (!widget.layout || header.includes(widget) || finite(widget.layout.y) + 0.5 < top) return
    others[widget.id] = { ...bareRect(widget.layout), y: Math.round(finite(widget.layout.y) + shift) }
  })
  return { moved: placed, others }
}

// Where a new (or copied) widget goes on a report page (Batch 3): the first free slot on the page at its
// footprint, else at fewer rows, else at the type minimum. `null` when not even the minimum fits above the page
// bottom; the caller then asks (a new page, or below the page anyway) instead of dropping it under the veil.
export function freeGridSlot(layout, widgets, grid, widget) {
  const min = widgetMinCells(widget?.type, widget)
  const cells = gridCells(layout, grid, { c: Math.min(grid.cols, min.c), r: min.r }, widget?.type === "header")
  const pageBottom = grid.margin + grid.rows * grid.rowPitch - grid.gutter + 0.5
  const sizes = [
    { cols: cells.cols, rows: cells.rows },
    { cols: cells.cols, rows: Math.min(cells.rows, min.r) },
    { cols: Math.min(cells.cols, Math.min(grid.cols, min.c)), rows: Math.min(cells.rows, min.r) },
  ]
  for (const size of sizes) {
    const slot = nearestFreeGridPlacement(gridLayout({ ...cells, ...size, top: undefined }, grid), widgets, grid)
    if (slot.y + slot.h <= pageBottom) return slot
  }
  return null
}

// Tidy on a report page (summer backlog #14): every widget on the 24 × 28 grid at no less than its type
// minimum (normalizeLayout, reading order), then compacted straight up (compactLayoutOnDrop: row groups keep
// one top, a pinned header stays). Columns never change, so the user's arrangement stays; only gaps close.
export function tidyGridLayout(widgets, page) {
  return compactGridWidgets(normalizeLayout(widgets, page), page)
}

// The widgets of a grid page compacted straight up (compactLayoutOnDrop: columns kept, row groups keep one top, a
// pinned header stays). After a delete (summer index.jsx :2966 runs compactLayoutVertical there, V2 DESIGN-LOG §34),
// the widgets below move up into the freed space. Unmoved widgets keep their identity. Pure.
/** @param {any[]} widgets @param {any} page @returns {any[]} */
export function compactGridWidgets(widgets, page) {
  if (!Array.isArray(widgets) || !widgets.length) return widgets || []
  const settled = compactLayoutOnDrop(Object.fromEntries(widgets.map((widget) => [widget.id, widget.layout])), page)
  return widgets.map((widget) =>
    sameLayout(settled[widget.id], widget.layout) ? widget : { ...widget, layout: { ...widget.layout, ...settled[widget.id] } },
  )
}

// "Arrange by type" (main's tidy, kept for pages nobody arranged): header and title full width, then KPIs in 4 (or 2)
// columns, filters in 3, charts in 2, tables full width, text and the rest in 2, in reading order within each type.
// Pixels on `canvasWidth`; a report page puts the result on its grid afterwards (tidyReportPage). Pure.
export function arrangeByTypeLayout(widgets, canvasWidth = 1123, isChart = () => false) {
  const gap = 24
  const margin = 24
  const contentWidth = canvasWidth - margin * 2
  const twoColumnWidth = Math.floor((contentWidth - gap) / 2)
  const threeColumnWidth = Math.floor((contentWidth - gap * 2) / 3)
  const fourColumnWidth = Math.floor((contentWidth - gap * 3) / 4)
  const sortedWidgets = [...(widgets || [])].sort(
    (first, second) => finite(first.layout?.y) - finite(second.layout?.y) || finite(first.layout?.x) - finite(second.layout?.x),
  )
  const headers = sortedWidgets.filter((widget) => widget.type === "header")
  const titles = sortedWidgets.filter((widget) => widget.type === "title")
  const kpis = sortedWidgets.filter((widget) => widget.type === "kpi")
  const filters = sortedWidgets.filter((widget) => widget.type === "filter")
  const charts = sortedWidgets.filter((widget) => isChart(widget.type))
  const tables = sortedWidgets.filter((widget) => widget.type === "table" || widget.type === "freeTable")
  const texts = sortedWidgets.filter((widget) => widget.type === "text")
  const placedIds = new Set([...headers, ...titles, ...kpis, ...filters, ...charts, ...tables, ...texts].map((widget) => widget.id))
  const others = sortedWidgets.filter((widget) => !placedIds.has(widget.id))
  const state = { y: margin, widgets: [] }
  const placeFullWidth = (widget, height) => {
    state.widgets.push({
      ...widget,
      layout: {
        ...widget.layout,
        x: widget.type === "header" ? 0 : margin,
        y: state.y,
        w: widget.type === "header" ? canvasWidth : contentWidth,
        h: height,
      },
    })
    state.y += height + gap
  }
  const placeGrid = (group, columns, width, height) => {
    group.forEach((widget, index) => {
      const column = index % columns
      const row = Math.floor(index / columns)
      state.widgets.push({
        ...widget,
        layout: { ...widget.layout, x: margin + column * (width + gap), y: state.y + row * (height + gap), w: width, h: height },
      })
    })
    if (group.length) state.y += Math.ceil(group.length / columns) * (height + gap)
  }
  headers.forEach((widget) => placeFullWidth(widget, Math.max(96, Math.min(widget.layout?.h || 96, 140))))
  titles.forEach((widget) => placeFullWidth(widget, Math.max(64, Math.min(widget.layout?.h || 72, 110))))
  placeGrid(kpis, canvasWidth >= 1000 ? 4 : 2, canvasWidth >= 1000 ? fourColumnWidth : twoColumnWidth, 150)
  placeGrid(filters, canvasWidth >= 1000 ? 3 : 2, canvasWidth >= 1000 ? threeColumnWidth : twoColumnWidth, 120)
  placeGrid(charts, canvasWidth >= 900 ? 2 : 1, canvasWidth >= 900 ? twoColumnWidth : contentWidth, 300)
  tables.forEach((widget) => placeFullWidth(widget, Math.max(300, Math.min(widget.layout?.h || 320, 420))))
  placeGrid(texts, canvasWidth >= 900 ? 2 : 1, canvasWidth >= 900 ? twoColumnWidth : contentWidth, 150)
  placeGrid(others, canvasWidth >= 900 ? 2 : 1, canvasWidth >= 900 ? twoColumnWidth : contentWidth, 180)
  return { widgets: state.widgets, bottom: state.y + margin }
}

// A page nobody has arranged yet (summer backlog #14): a widget has no layout, or saved widgets lie on top of each
// other (dropped in one spot). Such a page may be arranged by type; any other page keeps the user's (or the agent's)
// arrangement and is only tidied on the grid. HUNT P4: an agent page that is merely off the grid (its own pixels) is
// arranged, columns and all, so it is put on the grid, not rearranged by type.
export function pageNeedsArrange(widgets, page) {
  const list = (widgets || []).filter(Boolean)
  if (!list.length) return false
  if (list.some((widget) => !widget.layout || !Number.isFinite(Number(widget.layout.x)) || !Number.isFinite(Number(widget.layout.y)))) return true
  return layoutsCollide(list.map((widget) => ({ id: widget.id, layout: widget.layout })))
}

// Tidy on a report page: the grid-aware tidy (tidyGridLayout: on the grid, minimums, compacted straight up, columns
// kept). A page nobody arranged (pageNeedsArrange: no layouts, or widgets dropped in one spot) is arranged by type
// first, unless that puts more widgets past the page edge (as the editor shows the page) than the grid tidy does
// (HUNT P4: Tidy never pushes off the page what it can keep on it). `saved` is the page as saved.
export function tidyReportPage(saved, page, isChart = () => false) {
  const gridded = tidyGridLayout(saved, page)
  if (!pageNeedsArrange(saved, page)) return { mode: "grid", widgets: gridded }
  const arranged = new Map(arrangeByTypeLayout(saved, Number(page?.width) || 1123, isChart).widgets.map((widget) => [widget.id, widget]))
  // The saved order (paint and tab order) is kept; only the layouts change.
  const byType = tidyGridLayout(saved.map((widget) => arranged.get(widget.id) || widget), page)
  const bottom = (Number(page?.height) || 794) + 0.5
  const offPage = (widgets) => fitPageLayout(widgets, page, { squeeze: false }).filter((widget) => widget.layout.y + widget.layout.h > bottom).length
  return offPage(byType) <= offPage(gridded) ? { mode: "byType", widgets: byType } : { mode: "grid", widgets: gridded }
}

// Orientation switch on a report page (summer rescaleLayoutToCanvas, cf6407c92): x and w go through the grid columns
// (a widget on columns 12–23 stays on columns 12–23 of the new page); y and h are kept, so tables and KPIs never drop
// under their minimum heights. A full-bleed header keeps its edge-to-edge band. What no longer fits the page goes
// under the off-page veil, never piled at the bottom.
export function rescaleLayoutToCanvas(widgets, from, to) {
  const before = pageGrid(from)
  const after = pageGrid(to)
  return (widgets || []).map((widget) => {
    const layout = widget.layout || {}
    const cells = gridCells(layout, before, { c: 1, r: 1 }, widget.type === "header", true)
    const next = gridLayout({ ...cells, top: undefined }, after)
    return { ...widget, layout: { ...layout, x: next.x, w: next.w, y: finite(layout.y), h: finite(layout.h, next.h) } }
  })
}

// The Templates panel's pages, on the grid: their widget types and layouts (cells → pixels, then
// normalizeLayout). The panel draws its previews from these, and inserting a template places exactly these.
export const REPORT_TEMPLATES = ["overview", "analysis", "detail"]
// The table names each template slot looks for, in order (summer dataset_ref; resolveDatasetRef compares them
// without case or punctuation, then as part of a longer name). A client without such a table gets the slot unconnected.
export const TEMPLATE_REFS = {
  summary: ["summary", "key figures", "kpis", "fund summary", "portfolio summary", "overview"],
  performance: ["nav performance", "performance", "nav", "returns"],
  holdings: ["top holdings", "holdings", "positions"],
  allocation: ["sector allocation", "allocation", "sector", "asset allocation"],
}
/** @returns {{ type: string, layout: { x: number, y: number, w: number, h: number } }[]} */
export function reportTemplateLayout(name, page) {
  const grid = pageGrid(page)
  const band = HEADER_ROWS[Number(page?.height) > Number(page?.width) ? "portrait" : "landscape"]
  const rest = grid.rows - band
  const items =
    name === "overview"
      ? [
          { type: "kpi", ref: TEMPLATE_REFS.summary, cells: { col: 0, row: band, cols: 8, rows: 5 } },
          { type: "kpi", ref: TEMPLATE_REFS.summary, cells: { col: 8, row: band, cols: 8, rows: 5 } },
          { type: "kpi", ref: TEMPLATE_REFS.summary, cells: { col: 16, row: band, cols: 8, rows: 5 } },
          { type: "line", ref: TEMPLATE_REFS.performance, cells: { col: 0, row: band + 5, cols: 24, rows: rest - 5 } },
        ]
      : name === "analysis"
        ? [
            { type: "bar", ref: TEMPLATE_REFS.holdings, cells: { col: 0, row: band, cols: 12, rows: rest } },
            { type: "pie", ref: TEMPLATE_REFS.allocation, cells: { col: 12, row: band, cols: 12, rows: rest } },
          ]
        : [{ type: "table", ref: TEMPLATE_REFS.holdings, cells: { col: 0, row: band, cols: 24, rows: rest } }]
  const widgets = [{ type: "header", cells: { col: 0, row: 0, cols: 24, rows: band } }, ...items].map((item, index) => ({
    id: `template-${index}`,
    type: item.type,
    ref: item.ref,
    layout: gridLayout(item.cells, grid),
  }))
  return normalizeLayout(widgets, page).map((widget) => (widget.ref ? { type: widget.type, layout: widget.layout, ref: widget.ref } : { type: widget.type, layout: widget.layout }))
}

// Minimum pixel size of a widget on this page (Rnd minWidth/minHeight).
export function gridMinimumSize(type, grid, widget) {
  const min = widgetMinCells(type, widget)
  return {
    minWidth: Math.round(Math.min(grid.cols, min.c) * grid.colPitch - grid.gutter),
    minHeight: min.r * grid.rowPitch - grid.gutter,
  }
}

function finite(value, fallback = 0) {
  return value !== null && value !== "" && Number.isFinite(Number(value)) ? Number(value) : fallback
}

function cellsIntersect(first, second) {
  return (
    first.col < second.col + second.cols &&
    first.col + first.cols > second.col &&
    first.row < second.row + second.rows &&
    first.row + first.rows > second.row
  )
}
